/**
 * ExperimentListPage — MLflow 실험(Experiment) 목록 관리.
 *
 * 【역할】
 * - Runway/MLflow 실험 메타를 Oracle 도메인에 매핑한 관리 화면
 * - name/description/runwayRef/status/tags 로 실험 레지스트리 유지
 *
 * 【주요 기능】
 * - 검색 (status 필터)
 * - 인라인 CRUD (추가/삭제/저장)
 * - 상태 컬러 태그: DRAFT / RUNNING / COMPLETED / FAILED / ARCHIVED
 * - 행 클릭 → ExperimentDetailPage 로 이동 (/experiments/:id)
 *
 * 【사용 훅】
 * - useDataSet / useTransaction
 * - useLabel (t)
 *
 * 【Backend RPC serviceName】
 * - experiment/searchList  — 실험 목록
 * - experiment/save        — 벌크 저장 (C/U/D)
 *
 * 【DataSet 구조】
 * - ds_experiment: 실험 목록 (PK: experimentId, C/U/D)
 *
 * 【포팅 원본】 react-fastapi 신규 화면 (Runway/MLflow 연동 전용)
 */
import { useEffect, useMemo } from 'react'
import { Button, Card, Form, Input, Select, Space, Tag, Typography, message } from 'antd'
import { PlusOutlined, DeleteOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useDataSet, useTransaction } from '@/framework'
import type { TrackedRow } from '@/framework/useDataSet'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'

const { Title } = Typography

interface Experiment {
  experimentId?: number
  name: string
  description?: string
  runwayRef?: string
  status: 'DRAFT' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'ARCHIVED'
  metricsJson?: string
  tags?: string
  createdBy?: string
  createdAt?: string
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  RUNNING: 'processing',
  COMPLETED: 'success',
  FAILED: 'error',
  ARCHIVED: 'warning'
}

/**
 * ExperimentListPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ExperimentListPage() {
  const { t } = useLabel()
  const { transaction } = useTransaction()
  const [form] = Form.useForm()
  const listDs = useDataSet<Experiment>([])

  const search = () =>
    transaction({
      id: 'load',
      service: 'experiment/searchList',
      action: 'search',
      inds: { ds_search: form.getFieldsValue() },
      outds: { ds_experiment: listDs }
    })

  useEffect(() => {
    search()
  }, [])

  const add = () => listDs.addRow({ name: '', status: 'DRAFT' })
  const del = (rowId: string) => {
    const idx = listDs.rows.findIndex((r) => r._rowId === rowId)
    if (idx >= 0) listDs.deleteRow(idx)
  }

  const save = async () => {
    const res = await transaction({
      id: 'save',
      service: 'experiment/save',
      action: 'save',
      inds: { ds_experiment: listDs }
    })
    if (res.success) {
      void message.success(t('MSG_SAVE_SUCCESS', '저장되었습니다.'))
      search()
    }
  }

  const columns: ColumnsType<TrackedRow<Experiment>> = useMemo(
    () => [
      {
        title: t('LBL_EXPERIMENT_NAME', '실험명'),
        dataIndex: ['data', 'name'],
        render: (_v, r) => (
          <Input
            value={r.data.name}
            onChange={(e) => {
              const idx = listDs.rows.findIndex((x) => x._rowId === r._rowId)
              if (idx >= 0) listDs.updateRow(idx, { name: e.target.value })
            }}
            size="small"
          />
        )
      },
      {
        title: t('LBL_EXPERIMENT_STATUS', '상태'),
        dataIndex: ['data', 'status'],
        width: 140,
        render: (_v, r) => (
          <Select
            value={r.data.status}
            style={{ width: 120 }}
            size="small"
            onChange={(v) => {
              const idx = listDs.rows.findIndex((x) => x._rowId === r._rowId)
              if (idx >= 0) listDs.updateRow(idx, { status: v })
            }}
            options={[
              { value: 'DRAFT', label: 'DRAFT' },
              { value: 'RUNNING', label: 'RUNNING' },
              { value: 'COMPLETED', label: 'COMPLETED' },
              { value: 'FAILED', label: 'FAILED' },
              { value: 'ARCHIVED', label: 'ARCHIVED' }
            ]}
          />
        )
      },
      {
        title: t('LBL_RUNWAY_REF', 'Runway 참조'),
        dataIndex: ['data', 'runwayRef'],
        width: 140,
        render: (_v, r) => (
          <Input
            value={r.data.runwayRef}
            size="small"
            onChange={(e) => {
              const idx = listDs.rows.findIndex((x) => x._rowId === r._rowId)
              if (idx >= 0) listDs.updateRow(idx, { runwayRef: e.target.value })
            }}
          />
        )
      },
      {
        title: t('LBL_TAGS', '태그'),
        dataIndex: ['data', 'tags'],
        render: (_v, r) => (
          <Input
            value={r.data.tags}
            size="small"
            onChange={(e) => {
              const idx = listDs.rows.findIndex((x) => x._rowId === r._rowId)
              if (idx >= 0) listDs.updateRow(idx, { tags: e.target.value })
            }}
          />
        )
      },
      {
        title: t('LBL_EXPERIMENT_METRICS', '메트릭'),
        dataIndex: ['data', 'metricsJson'],
        width: 160,
        render: (_v, r) => (
          <code style={{ fontSize: 11, color: '#666' }}>
            {r.data.metricsJson ? String(r.data.metricsJson).slice(0, 40) : '-'}
          </code>
        )
      },
      {
        title: t('LBL_ACTION', '작업'),
        key: 'action',
        width: 80,
        render: (_v, r) => (
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => del(r._rowId)}
          />
        )
      }
    ],

    [t, listDs.rows]
  )

  const visibleRows = listDs.getVisibleRows()

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <Title level={3} style={{ margin: 0, marginBottom: 12 }}>
        {t('LBL_EXPERIMENT_LIST', '실험 관리')}
      </Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form form={form} layout="inline" onFinish={search}>
          <Form.Item name="keyword" label={t('LBL_KEYWORD', '검색어')}>
            <Input
              allowClear
              placeholder={t('PH_EXPERIMENT_NAME', '실험명')}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="status" label={t('LBL_EXPERIMENT_STATUS', '상태')}>
            <Select
              allowClear
              placeholder={t('PH_ALL', '전체')}
              style={{ width: 140 }}
              options={[
                { value: 'DRAFT', label: 'DRAFT' },
                { value: 'RUNNING', label: 'RUNNING' },
                { value: 'COMPLETED', label: 'COMPLETED' },
                { value: 'FAILED', label: 'FAILED' },
                { value: 'ARCHIVED', label: 'ARCHIVED' }
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<ReloadOutlined />}>
                {t('BTN_SEARCH', '조회')}
              </Button>
              <Button icon={<PlusOutlined />} onClick={add}>
                {t('BTN_ADD', '추가')}
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                {t('BTN_SAVE', '저장')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Space style={{ flexShrink: 0 }} size={4} wrap>
        <Tag color="blue">
          {t('LBL_TOTAL', '총')}: {visibleRows.length}
        </Tag>
        {Object.entries(STATUS_COLORS).map(([status, color]) => {
          const count = visibleRows.filter((r) => r.data.status === status).length
          return count > 0 ? (
            <Tag key={status} color={color}>
              {status}: {count}
            </Tag>
          ) : null
        })}
      </Space>
      <DataTable<TrackedRow<Experiment>>
        rowKey={(r) => r._rowId}
        columns={columns}
        dataSource={visibleRows}
        size="small"
      />
    </PageContainer>
  )
}
